import React, { useState } from 'react';
import {
  Row,
  Col,
  Typography,
  List,
  Button,
  Card,
  InputNumber,
  Divider,
  Popover,
  Switch,
  Space,
} from 'antd';
import {
  CheckCircleFilled,
  ArrowLeftOutlined,
  CreditCardOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const TeamsPlanReview = ({
  plan,
  onBack,
  onContinue,
  billingCycle,
  setBillingCycle,
  quantity,
  setQuantity,
  minimumQuantity,
}) => {
  // Get the appropriate price based on the plan type
  const getPrice = (isAnnual) => {
    // Apply specific discounts based on the original price
    if (plan.price === 49.99) {
      return isAnnual ? 39.99 : 49.99;
    } else if (plan.price === 99.99) {
      return isAnnual ? 79.99 : 99.99;
    } else {
      // For any other price, calculate a 20% discount
      return isAnnual ? plan.price * 0.8 : plan.price;
    }
  };

  const basePrice = getPrice(false); // Original monthly price
  const discountedPrice = getPrice(true); // Discounted annual price (per month)

  // Calculate total based on billing cycle
  const pricePerUser = billingCycle === 'annual' ? discountedPrice : basePrice;
  const totalPrice = pricePerUser * quantity;
  const annualTotalPrice = totalPrice * 12;

  // Calculate savings percentage
  const savingsPercentage = Math.round((1 - discountedPrice / basePrice) * 100);

  return (
    <div>
      <div className="flex justify-between items-center mr-4 pr-4 mb-4">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          className="text-[#13C17F] hover:text-[#0F9F67]"
        >
          Back to plans
        </Button>

        <Space align="center">
          <Text>Monthly</Text>
          <Switch
            checked={billingCycle === 'annual'}
            onChange={(checked) =>
              setBillingCycle(checked ? 'annual' : 'monthly')
            }
            className={billingCycle === 'annual' ? 'bg-[#13C17F]' : ''}
          />
          <Text>
            Annual <Text type="success">(Save {savingsPercentage}%)</Text>
          </Text>
        </Space>
      </div>

      <Title level={2} className="text-center mb-8">
        Review plan
      </Title>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card className="h-full">
            <List
              dataSource={plan.features}
              renderItem={(item) => (
                <List.Item>
                  <CheckCircleFilled
                    style={{ color: '#0F9F67', marginRight: '8px' }}
                  />
                  {item.feature}
                  <Popover content={item.description} trigger="hover">
                    <QuestionCircleOutlined
                      style={{ marginLeft: '8px', color: '#0F9F67' }}
                    />
                  </Popover>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card className="h-full">
            <div className="mb-6">
              <Text strong>Number of Users:</Text>
              <div className="mt-2">
                <InputNumber
                  min={minimumQuantity}
                  value={quantity}
                  onChange={setQuantity}
                  style={{ width: '100%' }}
                  size="large"
                />
              </div>
            </div>

            <Divider />

            <div className="mb-6">
              <Title level={4}>Order Summary</Title>
              <Row justify="space-between" className="mb-2">
                <Col>Price</Col>
                <Col>
                  ${pricePerUser.toFixed(2)}/month per user
                  {billingCycle === 'annual' && (
                    <Text type="success">
                      {' '}
                      (${(basePrice - discountedPrice).toFixed(2)} saved)
                    </Text>
                  )}
                </Col>
              </Row>
              <Row justify="space-between" className="mb-2">
                <Col>Number of Users</Col>
                <Col>{quantity}</Col>
              </Row>
              <Divider />
              <Row justify="space-between">
                <Col>
                  <Text strong>
                    {billingCycle === 'annual'
                      ? 'Total Annual Charge'
                      : 'Total Monthly Charge'}
                  </Text>
                </Col>
                <Col>
                  <Text strong className="text-[#13C17F]">
                    $
                    {billingCycle === 'annual'
                      ? annualTotalPrice.toFixed(2)
                      : totalPrice.toFixed(2)}
                    /{billingCycle === 'annual' ? 'year' : 'month'}
                  </Text>
                </Col>
              </Row>
              {billingCycle === 'annual' && (
                <Row justify="space-between" className="mt-2">
                  <Col>
                    <Text type="secondary">Monthly equivalent</Text>
                  </Col>
                  <Col>
                    <Text type="secondary">
                      ${(annualTotalPrice / 12).toFixed(2)}/month
                    </Text>
                  </Col>
                </Row>
              )}
            </div>

            <div className="text-center mt-6">
              <Button
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
                onClick={onContinue}
                className="w-full bg-[#13C17F] hover:bg-[#0F9F67] text-white"
                style={{
                  height: '48px',
                  fontSize: '18px',
                  borderRadius: '12px',
                }}
              >
                Make Payment
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default TeamsPlanReview;
